// Types
import { QueryString, QueryStringParameterFilter, UrlParameterFilter } from "interfaces/shared/queryString";
import { ParsedUrlQuery } from "querystring";
import { Category, CurrentSelectedCategory } from "interfaces/searchPage/category";
import {SeoInformation} from "interfaces/searchPage/seoInformations";

// Constants
import { QueryStringPrimaryKeys } from "constants/searchPage/urlEnum";
import { queryStringKeys } from "constants/searchPage/apiQueryParam";

// Utils
import { addActiveTagToList, findTagCategoryById } from "utils/searchPage/categories";
import {parsePrimaryKeysQueryString} from "utils/searchPage/filters";

interface AppendQueryStringParams {
	routerQuery: ParsedUrlQuery;
	queryString: QueryString;
	removePaginationFilter?: boolean;
}

export const appendQueryStringPrimaryKeysInUrl = (appendParams: AppendQueryStringParams): ParsedUrlQuery => {
	const { routerQuery, queryString, removePaginationFilter } = appendParams;
	const cleanedQueryString = deleteQueryStringExceptArgumentKeys(routerQuery, queryStringKeys, removePaginationFilter);
	return { ...cleanedQueryString, [queryString.queryStringKey]: queryString.queryStringValue };
};

// Used for query object from useRouter()
export const deleteQueryStringExceptArgumentKeys = (queryString: ParsedUrlQuery, qsk: QueryStringPrimaryKeys[], removePagination = false): ParsedUrlQuery => {
	[...Object.keys(queryString)].forEach((param) => {
		if (!qsk.includes(param as QueryStringPrimaryKeys) && queryString[param] && param !== QueryStringPrimaryKeys.PAGE) {
			delete queryString[param];
		}
		if (removePagination && param === QueryStringPrimaryKeys.PAGE) {
			delete queryString[param];
		}
	});
	return queryString;
};

// Used for query object from useRouter()
export const removeSpecificKeyFromQueryString = (queryString: ParsedUrlQuery, queryStringKey: QueryStringPrimaryKeys): ParsedUrlQuery => {
	delete queryString[queryStringKey];
	return queryString;
};

// Used for url as string
export const removeParamsFromUrl = (targetUrl: string, queryStringKey: string): string => {
	if (!targetUrl) {
		return "";
	}
	const url = new URL(targetUrl);
	const newQueryString = removeParameterFromUrl(url.searchParams.toString(), queryStringKey);
	return `${url.origin}${url.pathname}?${newQueryString}`;
};

export const getCompleteUrlAsPathFromRouter = (path: string): string => `${process.env.NEXT_PUBLIC_DOMAIN}/${path}`;

export const getCompleteUrlAsPath = (path: string): string => `${process.env.NEXT_PUBLIC_DOMAIN}/${process.env.NEXT_PUBLIC_SEARCH_PATH_NAME}/${path}`;

export const getUrlWithoutQueryParams = (url: string): string => {
	return url.split("?")[0];
};

export const getQueryStringParamsByKeys = (path: string, queryStringKey: QueryStringPrimaryKeys) => {
	const primaryKeysQueryString: QueryString[] = parsePrimaryKeysQueryString(path.split("?")[1]);
	return primaryKeysQueryString.filter((qs) => qs.queryStringKey === queryStringKey).pop()?.queryStringValue || "";
}

export const checkIfQueryStringExists = (queryString: ParsedUrlQuery, queryStringKey: QueryStringPrimaryKeys): string | undefined => {
	return [...Object.keys(queryString)].find((param) => param === queryStringKey);
};

export const getParameterInUrl = (urlPath: string, referenceParameter: string): UrlParameterFilter | null => {
	const regex = new RegExp(`${referenceParameter}([0-9]+)`);
	const match = regex.exec(urlPath);
	if (match !== null) {
		return { parameter: referenceParameter, tagId: parseInt(match[1], 10) };
	}
	return null;
};

export const takeCurrentActiveFilters = (macroReferenceParameter: string, currentSelectedCategory: CurrentSelectedCategory, urlPath: string): UrlParameterFilter[] => {
	const parametersInUrlPath = matchReferenceParametersInUrlPath(urlPath); // take existed filter, except currentSelectedCategory
	parametersInUrlPath.push({ parameter: macroReferenceParameter, tagId: currentSelectedCategory.selectedTags[0].id }); // add currentSelectedCategory
	return parametersInUrlPath;
};

export const matchReferenceParametersInUrlPath = (urlPath: string): UrlParameterFilter[] => {
	const regex = /([a-z])([0-9]+)/gm;
	const foundFilter: UrlParameterFilter[] = [];
	const matches = urlPath.matchAll(regex);
	for (const match of matches) {
		foundFilter.push({ parameter: match[1], tagId: parseInt(match[2], 10) });
	}
	return foundFilter;
};

export const buildPathname = (categories: Category[]): string => {
	const joinedSlugsName = categories.map((category) => category.tags[0]?.slugName).join("-");
	const joinedParametersIds = categories.map((category) => `${category.urlParameter}${category.tags[0]?.id}`).join("");
	return `${joinedSlugsName}-${joinedParametersIds}`;
};

export const existFilterInQueryString = (referenceParameter: string, urlPath: string): string[] => {
	const regex = referenceParameter === "sort" ? new RegExp(`sort=(([a\\-z]+)|([a-z]+))`) : new RegExp(`${referenceParameter}=[0-9,]+`);
	const queryString = urlPath.split("?")[1] || "";
	const queryStringMatch = queryString.match(regex);
	if (referenceParameter === "sort") {
		return queryStringMatch ? [queryStringMatch[0].split("=")[1]] : [];
	} else {
		return queryStringMatch ?? [];
	}
};

export const updateFiltersUrl = (urlPath: string, currentSelectedCategory: CurrentSelectedCategory, currentIntersectedCategories: Category[]) => {
	const parameterInUrl = getParameterInUrl(urlPath, currentSelectedCategory.macroCategory.urlParameter);
	let updatedPathname;
	let updatedQueryString;
	if (currentSelectedCategory.selectedTags.length) {
		if (parameterInUrl?.parameter) {
			updatedPathname = handleFilterParameterInPathname(currentSelectedCategory, parameterInUrl, urlPath);
		} else {
			updatedPathname = handleNewFilterParameterInPathname(currentSelectedCategory, urlPath, currentIntersectedCategories);
		}
		if (currentSelectedCategory.selectedTags.length > 1) {
			if (existFilterInQueryString(currentSelectedCategory.macroCategory.urlParameter, urlPath).length > 0) {
				updatedQueryString = handleFilterParameterInQueryString(currentSelectedCategory, urlPath);
			} else {
				updatedQueryString = handleNewFilterParameterInQueryString(currentSelectedCategory, urlPath, currentIntersectedCategories);
			}
		} else {
			updatedQueryString = removeFilterParameterInQueryString(currentSelectedCategory.macroCategory.urlParameter, urlPath);
		}
	} else {
		updatedPathname = removeFilterParameterInPathname(currentSelectedCategory.macroCategory.urlParameter, urlPath, currentIntersectedCategories, parameterInUrl);
		updatedQueryString = removeFilterParameterInQueryString(currentSelectedCategory.macroCategory.urlParameter, urlPath);
	}
	if (updatedQueryString && updatedQueryString.includes(QueryStringPrimaryKeys.PAGE)) {
		updatedQueryString = removeParameterFromUrl(updatedQueryString, QueryStringPrimaryKeys.PAGE);
	}
	return [updatedPathname, updatedQueryString];
};

export const handleFilterParameterInPathname = (currentSelectedCategory: CurrentSelectedCategory, parameterInUrl: UrlParameterFilter, urlPath: string): string | undefined => {
	const macroReferenceParameter = currentSelectedCategory.macroCategory.urlParameter;
	const activeTagInUrl = currentSelectedCategory.macroCategory.tags.find((tag) => tag.id === parameterInUrl.tagId);
	if (
		activeTagInUrl &&
		(activeTagInUrl.seoUrlOrder > currentSelectedCategory.selectedTags[0].seoUrlOrder || !currentSelectedCategory.selectedTags.map((tag) => tag.id).includes(activeTagInUrl.id))
	) {
		const updatedPathname = urlPath
			.replace(activeTagInUrl.slugName, currentSelectedCategory.selectedTags[0].slugName)
			.replace(new RegExp(`${macroReferenceParameter}[0-9]+`), `${macroReferenceParameter}${currentSelectedCategory.selectedTags[0].id}`);
		return getUrlWithoutQueryParams(updatedPathname);
	} else {
		return getUrlWithoutQueryParams(urlPath);
	}
};

export const handleNewFilterParameterInPathname = (currentSelectedCategory: CurrentSelectedCategory, urlPath: string, currentIntersectedCategories: Category[]): string => {
	const parametersInUrlPath = takeCurrentActiveFilters(currentSelectedCategory.macroCategory.urlParameter, currentSelectedCategory, urlPath);
	const mapOnlyReferenceParametersInUrl = parametersInUrlPath.map((parameterUrl) => parameterUrl.parameter);
	const filteredCategories = currentIntersectedCategories
		.filter((category) => mapOnlyReferenceParametersInUrl.includes(category.urlParameter))
		.sort((category1, category2) => category1.seoUrlOrder - category2.seoUrlOrder)
		.map((filteredCategory) => {
			const category = { ...filteredCategory };
			const parameterInUrlFilter = parametersInUrlPath.find((parameterInUrlPath) => parameterInUrlPath.parameter === category.urlParameter);
			if (parameterInUrlFilter) {
				category.tags = filteredCategory.tags.filter((tag) => tag.id === parameterInUrlFilter.tagId);
			}
			return category;
		});
	return buildPathname(filteredCategories);
};

export const handleFilterParameterInQueryString = (currentSelectedCategory: CurrentSelectedCategory, urlPath: string): string => {
	const macroReferenceParameter = currentSelectedCategory.macroCategory.urlParameter;
	const currentSelectedTags = currentSelectedCategory.selectedTags.map((tag) => tag.id).join(",");
	const queryString = urlPath?.split("?")[1] || "";
	return queryString.replace(new RegExp(`${macroReferenceParameter}=[0-9,]+`), `${macroReferenceParameter}=${currentSelectedTags}`);
};

export const handleNewFilterParameterInQueryString = (
	currentSelectedCategory: CurrentSelectedCategory,
	urlPath: string,
	currentIntersectedCategories: Category[]
): string | undefined => {
	const queryString = urlPath.split("?")[1] || "";
	const splitQueryString = queryString.split("&");
	const queryStringPrimaryKeys = splitQueryString.filter((queryParam) =>
		Object.values(QueryStringPrimaryKeys)
			.map((key) => key.toString())
			.includes(queryParam.split("=")[0])
	);
	const splitCurrentQueryString: string[] = splitQueryString.filter(
		(queryParam) => queryParam !== "" && !queryStringPrimaryKeys.includes(queryParam)
	); // take existed querystring filters array

	splitCurrentQueryString.push(
		`${currentSelectedCategory.macroCategory.urlParameter}=${currentSelectedCategory.selectedTags.map((tag) => tag.id).join(",")}`
	); // add current parameter to existed filter array

	const parameterInCurrentQueryString = splitCurrentQueryString.map((queryParam) => queryParam.split("=")[0]);
	const arrayBuilder: string[] = [];
	currentIntersectedCategories.forEach((category) => {
		if (parameterInCurrentQueryString.includes(category.urlParameter)) {
			arrayBuilder.push(splitCurrentQueryString.find((queryParam) => queryParam.includes(category.urlParameter)) || "");
		}
	});
	arrayBuilder.push(...queryStringPrimaryKeys);
	return arrayBuilder.join("&");
};

export const removeFilterParameterInPathname = (
	macroReferenceParameter: string,
	urlPath: string,
	currentIntersectedCategories: Category[],
	parameterInUrl: UrlParameterFilter | null
): string | undefined => {
	if (parameterInUrl?.parameter) {
		let updatedPathname = urlPath.replace(new RegExp(`${macroReferenceParameter}${parameterInUrl.tagId}`), "");
		const tagInPathnameUrl = [...currentIntersectedCategories]
			.filter((category) => category.urlParameter === macroReferenceParameter)
			.map((category) => category.tags.find((tag) => tag.id === parameterInUrl.tagId))
			.pop();
		if (tagInPathnameUrl) {
			updatedPathname = updatedPathname.replace(new RegExp(`${tagInPathnameUrl.slugName}-?`), "");
		}
		return getUrlWithoutQueryParams(updatedPathname);
	}
};

export const removeFilterParameterInQueryString = (macroReferenceParameter: string, urlPath: string): string | undefined => {
	const queryString = urlPath?.split("?")[1] || "";
	if (existFilterInQueryString(macroReferenceParameter, urlPath).length > 0) {
		const regexMiddleParameterInQueryString = new RegExp(`&${macroReferenceParameter}=[0-9,]+&`);
		let updatedQueryString = "";
		if (queryString.match(regexMiddleParameterInQueryString)) {
			updatedQueryString = queryString.replace(regexMiddleParameterInQueryString, "&");
		} else {
			updatedQueryString = queryString.replace(new RegExp(`&?${macroReferenceParameter}=[0-9,]+&?`), "");
		}
		return updatedQueryString;
	} else {
		return queryString;
	}
};

export const handleQuerystringParams = (macroCategory: Category, currentTagId: number, tagsActiveCurrently: Category[], urlPath: string) => {
	const valuesQueryString = getQueryStringGroupedValue(urlPath);
	if (valuesQueryString.length) {
		const valuesQsByReferenceParameter = valuesQueryString.find((valueQs) => valueQs.parameter === macroCategory.urlParameter);
		if (valuesQsByReferenceParameter) {
			valuesQsByReferenceParameter.tagIds
				.filter((tagId) => tagId !== currentTagId)
				.forEach((tagId) => {
					const qsTag = findTagCategoryById(macroCategory, tagId);
					addActiveTagToList(tagsActiveCurrently, qsTag, tagId);
				});
		}
	}
};

export const getQueryStringGroupedValue = (urlPath: string): QueryStringParameterFilter[] => {
	const foundFilter: QueryStringParameterFilter[] = [];
	const queryString = urlPath?.split("?")[1] || "";
	if (queryString) {
		const valuesQueryString = queryString.split("&");
		valuesQueryString.forEach((valueQueryString) => {
			const [parameter, tagIds] = valueQueryString.split("=");
			if (parameter && tagIds) {
				foundFilter.push({
					parameter,
					tagIds: tagIds.split(",").map((tagId) => parseInt(tagId, 10)),
				});
			}
		});
	}
	return foundFilter;
};

export const filterQueryStringByAllowedTagParams = (urlPath: string, allowedParameters: string[]): string => {
	const relativePath = urlPath.split('?')[0];
	const queryStringFilters = getQueryStringGroupedValue(urlPath);

	const filteredQueryStringFilters = queryStringFilters.filter((filter) =>
		allowedParameters.includes(filter.parameter)
	);

	const filteredQueryString = filteredQueryStringFilters.map((filter) =>
		`${filter.parameter}=${filter.tagIds.join(',')}`).join('&');

	return filteredQueryString ? `${relativePath}?${filteredQueryString}` : relativePath;
};

export const filterQueryStringByAllowedParams = (urlPath: string, allowedTagParameters: string[], allowedSimpleParameters: string[]): string => {
	const [relativePath, originalQueryString] = urlPath.split('?');
	const urlBase = relativePath;

	const filteredTagPart = filterQueryStringByAllowedTagParams(urlPath, allowedTagParameters);
	const tagQueryString = filteredTagPart.split('?')[1] || '';

	const params = new URLSearchParams(originalQueryString);
	const simpleQueryStringParts: string[] = [];

	for (const param of allowedSimpleParameters) {
		const value = params.get(param);
		if (value !== null) {
			simpleQueryStringParts.push(`${param}=${value}`);
		}
	}

	const combinedParts = [tagQueryString, ...simpleQueryStringParts].filter(Boolean);
	return combinedParts.length > 0 ? `${urlBase}?${combinedParts.join('&')}` : urlBase;
};

export const createStringFromQueryObject = (queryObject: ParsedUrlQuery): string => {
	if (Object.keys(queryObject).length) {
		const slugPage = `${queryObject["slug"] || ""}`;
		const queryStringForPath: string[] = [];
		[...Object.keys(queryObject)].forEach((queryKey) => {
			if (queryKey !== "slug") {
				queryStringForPath.push(`${queryKey}=${queryObject[queryKey]}&`);
			}
		});
		if (queryStringForPath.length) {
			queryStringForPath[queryStringForPath.length - 1] = queryStringForPath[queryStringForPath.length - 1].slice(0, -1);
			return `${slugPage}?${queryStringForPath.join("")}`;
		} else {
			return slugPage;
		}
	} else {
		return "";
	}
};

export const appendQueryStringParameterInUrl = (queryObject: ParsedUrlQuery, newQueryString: QueryString, removePagination = false): ParsedUrlQuery => {
	queryObject[newQueryString.queryStringKey] = newQueryString.queryStringValue;
	if (removePagination && Object.keys(queryObject).includes(QueryStringPrimaryKeys.PAGE)) {
		delete queryObject[QueryStringPrimaryKeys.PAGE];
	}
	return queryObject;
};

export const changePageNumberInQueryString = (urlPath: string, numberPage: number): string => {
	const urlSplit = urlPath.split("?");
	const urlSearchParams = new URLSearchParams(urlSplit[1]);
	if (numberPage === 1) {
		urlSearchParams.delete(QueryStringPrimaryKeys.PAGE);
	} else {
		urlSearchParams.set(QueryStringPrimaryKeys.PAGE, numberPage.toString());
	}
	const queryString = decodeURIComponent(urlSearchParams.toString());
	return queryString ? `${urlSplit[0]}?${queryString}` : urlSplit[0];
};

export const removeParameterFromUrl = (queryString: string, parameter: string): string => {
	const urlSearchParams = new URLSearchParams(queryString);
	urlSearchParams.delete(parameter);
	return decodeURIComponent(urlSearchParams.toString());
};

export const getNextUrl = (macroCategory: Category, selectedTags: Category[], urlPath: string, categories: Category[]): string => {
	let nextUrl = "";
	const urlParts = updateFiltersUrl(urlPath, { macroCategory, selectedTags }, categories);
	if (typeof urlParts[0] === "string") {
		if (urlParts[1]) {
			nextUrl = `${urlParts[0]}?${urlParts[1]}`;
		} else {
			nextUrl = urlParts[0];
		}
	}
	return nextUrl;
};

export const getSeoInfoPageFromServerSideFunction = (activeFilters: CurrentSelectedCategory[], seoInformations: SeoInformation[], path: string): Partial<SeoInformation> => {
	if (path) {
		const currentUrl = getCompleteUrlAsPath(path);
		const matchedSeoInfo = seoInformations.filter((seoInfo) => getUrlWithoutQueryParams(seoInfo.url) === getUrlWithoutQueryParams(currentUrl)).pop();
		if (matchedSeoInfo) {
			const newSeoUrl = removeParamsFromUrl(matchedSeoInfo.url, QueryStringPrimaryKeys.PRODUCTS_VISIBILITY);
			return { ...matchedSeoInfo, url: newSeoUrl };
		} else {
			const seoTitle = activeFilters
				.flatMap((filter) => filter.selectedTags[0])
				.map((tag) => tag.name)
				.join(" ");
			return {
				url: removeParamsFromUrl(currentUrl, QueryStringPrimaryKeys.PRODUCTS_VISIBILITY),
				title: seoTitle,
				h1: seoTitle
			};
		}
	}
	return { url: "", title: "", h1: "" };
};

export const isValidPath = (path: string, selectedCategories: CurrentSelectedCategory[]): boolean => {
	const pathNoQueryString = path.split('?')[0];

	if (pathNoQueryString.length === 0) {
		return true;
	}

	const hasValidPath = !pathNoQueryString.includes('undefined');
	const hasSelectedCategories = selectedCategories.length > 0;

	return hasValidPath && hasSelectedCategories;
};


export const hasQuerySearch = (querySearch: string): boolean => {
	return querySearch !== '' && querySearch !== '*';
}

export const encodeQueryStringValue = (value: string): string => {

	return value
		.replaceAll(/ /g, '+')
		.replaceAll('&', '%26')
		.replaceAll('#', '%23');
}

export const encodeTwiceQueryStringValue = (value: string): string => {
	return value
		.replaceAll('%26', '%2526')
		.replaceAll('%23','%2523')
}
